const RAW_EXTENSIONS = new Set([
  'nef', 'cr2', 'cr3', 'arw', 'dng', 'orf', 'raf', 'rw2', 'srw', 'pef', 'x3f',
  'raw', 'psd', 'bmp', 'tif', 'tiff',
]);

/** Extensions we never accept, even if the picker or MIME claims image/* */
const BLOCKED_EXTENSIONS = new Set([
  ...RAW_EXTENSIONS,
  'gif', 'svg', 'svgz',
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
  'zip', 'rar', '7z', 'gz', 'tar',
  'mp4', 'mov', 'm4v', 'avi', 'mkv', 'webm', 'mp3', 'wav', 'aac',
  'html', 'htm', 'js', 'json', 'xml', 'txt', 'csv',
  'exe', 'dmg', 'apk', 'ipa',
]);

const HEIC_BRANDS = new Set(['heic', 'heix', 'hevc', 'hevx', 'mif1', 'msf1']);

export const CLIENT_MAX_BYTES = 30 * 1024 * 1024;
export const TARGET_MAX_BYTES = 2.5 * 1024 * 1024;
export const MAX_EDGE = 3000;

/** File picker accept list — still validated by magic bytes after selection. */
export const MEMORIES_ACCEPT_IMAGES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  'image/avif',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.heic',
  '.heif',
  '.avif',
].join(',');

function bytesToHex(bytes, start, end) {
  return Array.from(bytes.slice(start, end))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function ascii(bytes, start, end) {
  return String.fromCharCode(...bytes.slice(start, end));
}

export function sniffImageBytes(bytes, originalName = '') {
  const ext = String(originalName).split('.').pop()?.toLowerCase() || '';
  if (BLOCKED_EXTENSIONS.has(ext) || RAW_EXTENSIONS.has(ext)) {
    if (RAW_EXTENSIONS.has(ext) || ['tif', 'tiff', 'bmp', 'psd', 'raw'].includes(ext)) {
      return { kind: 'raw', reason: 'RAW_UNSUPPORTED' };
    }
    return { kind: 'blocked', reason: 'UNSUPPORTED_TYPE' };
  }
  if (!bytes || bytes.length < 12) {
    return { kind: 'unknown', reason: 'UNSUPPORTED_TYPE' };
  }

  if (bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) {
    return { kind: 'jpeg' };
  }
  if (bytesToHex(bytes, 0, 8) === '89504e470d0a1a0a') {
    return { kind: 'png' };
  }
  if (ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 12) === 'WEBP') {
    return { kind: 'webp' };
  }
  // GIF87a / GIF89a
  if (ascii(bytes, 0, 3) === 'GIF' && bytes[3] === 0x38) {
    return { kind: 'gif', reason: 'UNSUPPORTED_TYPE' };
  }
  // PDF
  if (ascii(bytes, 0, 4) === '%PDF') {
    return { kind: 'pdf', reason: 'UNSUPPORTED_TYPE' };
  }
  if (ascii(bytes, 4, 8) === 'ftyp') {
    const brand = ascii(bytes, 8, 12);
    if (brand === 'crx') return { kind: 'raw', reason: 'RAW_UNSUPPORTED' };
    if (brand === 'avif') return { kind: 'avif' };
    if (HEIC_BRANDS.has(brand)) return { kind: 'heic' };
    // Other ISO-BMFF (mp4, mov, etc.)
    return { kind: 'video', reason: 'UNSUPPORTED_TYPE' };
  }
  if (
    (bytes[0] === 0x49 && bytes[1] === 0x49 && bytes[2] === 0x2A && bytes[3] === 0x00)
    || (bytes[0] === 0x4D && bytes[1] === 0x4D && bytes[2] === 0x00 && bytes[3] === 0x2A)
  ) {
    return { kind: 'raw', reason: 'RAW_UNSUPPORTED' };
  }
  const text = Array.from(bytes.slice(0, 256))
    .map((b) => String.fromCharCode(b))
    .join('')
    .trimStart()
    .toLowerCase();
  if (text.startsWith('<svg') || (text.startsWith('<?xml') && text.includes('<svg'))) {
    return { kind: 'svg', reason: 'UNSUPPORTED_TYPE' };
  }
  return { kind: 'unknown', reason: 'UNSUPPORTED_TYPE' };
}

export function rejectCopy(reason, size) {
  if (reason === 'RAW_UNSUPPORTED') {
    return 'Camera RAW isn\'t supported. Export a JPEG and try again.';
  }
  if (reason === 'TOO_LARGE') {
    const mb = Math.round((size || 0) / (1024 * 1024));
    return `This file is ${mb} MB. The limit is 30 MB.`;
  }
  return 'That file type isn\'t supported. Use JPEG, PNG, HEIC, or WebP.';
}

export function isAllowedImageKind(kind) {
  return ['jpeg', 'png', 'webp', 'heic', 'avif'].includes(kind);
}
